import React, { useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { IconButton, Tooltip } from '@mui/material'
import { Close } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { M_MessageBoard, M_Button, BufferService, LoadingContext, M_StateActions, NavigateContext, MSwitchButton, MTimeLine, M_Header, M_InputTextField } from 'src/components/M_Components'
import { useDispatch, useSelector } from 'react-redux'
import './orderServices.css'

const buildGlobalFilter = (value) => {
    const normalizedValue = value.trim().replace(/'/g, "''")
    if (!normalizedValue) return ''

    const filters = [
        `contains(Customer/Name, '${normalizedValue}')`,
        `contains(Customer/CustomerCode, '${normalizedValue}')`,
        `contains(ServiceType/Name, '${normalizedValue}')`,
        `contains(ServiceOrderState/Name, '${normalizedValue}')`,
        `contains(ReferenceInvoiceClient, '${normalizedValue}')`,
        // `contains(Required, '${normalizedValue}')`,
        `contains(NotesDriver, '${normalizedValue}')`,
        `contains(NotesInternal, '${normalizedValue}')`
    ]

    if (/^\d+$/.test(normalizedValue)) {
        filters.push(`ServiceOrderId eq ${normalizedValue}`, `DocNumber eq ${normalizedValue}`)
    }

    return `(${filters.join(' or ')})`
}

export default function OrderSevices() {
    const dispatch = useDispatch()
    const M_State = useSelector((state) => state.StateKeyValue)
    const [id, setId] = useState(M_State.filter(obj => obj.key === 'selectedTab')[0]?.value)
    const [orderServices, setOrderServices] = useState([])
    const [addressTimeline, setAddressTimeline] = useState([])
    const [dialogDelete, setDialogDelete] = useState(false)
    const [dialogInformation, setDialogInformation] = useState(false)
    const [autoRefresh, setAutoRefresh] = useState(false)
    const [selectedOrderService, setSelectedOrderService] = useState(null)
    const [isFirstLoop, setIsFirstLoop] = useState(true)
    const [searchValue, setSearchValue] = useState('')
    const [debouncedSearchValue, setDebouncedSearchValue] = useState('')
    const ctx = useContext(LoadingContext)
    const ctx2 = useContext(NavigateContext)
    const { t } = useTranslation()
    const bufferServiceOrderService = useMemo(() => new BufferService('ServiceOrder'), [])
    const sessionKeys = ['id', 'orderServices', 'addressTimeline', 'autoRefresh', 'page', 'hasMore', 'searchValue']
    const pageSize = 20
    const [page, setPage] = useState(1)
    const [loading, setLoading] = useState(false)
    const [hasMore, setHasMore] = useState(true)
    const sentinelRef = useRef(null)
    const isFetchingRef = useRef(false)
    const requestGenerationRef = useRef(0)
    const searchInitializedRef = useRef(false)
    const reloadOrderServiceRef = useRef(null)

    useEffect(() => {
        if (isFirstLoop) {
            ctx.setIsLoad(true)
            getSessionStorageData(id)
            setIsFirstLoop(false)
        }
        // eslint-disable-next-line
        let interval

        if (autoRefresh) {
            interval = setInterval(() => {
                reloadOrderServiceRef.current?.()
            }, 10000)
        }

        return () => clearInterval(interval)

    }, [autoRefresh, isFirstLoop, id, debouncedSearchValue])

    useEffect(() => {
        const newId = M_State.filter(obj => obj.key === 'selectedTab')[0]?.value
        if (newId !== id) {
            setId(newId)
            getSessionStorageData(newId)
        }
    }, [M_State, id])

    useEffect(() => {
        const tempArray = []
        sessionKeys.forEach((key) => {
            tempArray.push(sessionStorageMachine(id, key, key))
        })
        dispatch(M_StateActions.addM_State({ key: id, value: tempArray }))
    }, [id, orderServices, addressTimeline, autoRefresh, page, hasMore, searchValue])

    const sessionStorageMachine = (id, key, value) => {
        let tempObj = {}
        if (eval(value) instanceof Map) {
            tempObj = { key: `${key}`, value: (Array.from(eval(value))) }
        } else {
            tempObj = { key: `${key}`, value: (eval(value)) }
        }
        return tempObj
    }

    const getSessionStorageData = (sessionID) => {
        ctx.setIsLoad(true)
        const M_StateData = M_State.filter(obj => obj.key === sessionID)[0]?.value
        if (M_StateData) {
            const tempOS = M_StateData.filter(obj => obj.key === `orderServices`)[0]?.value
            const tempAT = M_StateData.filter(obj => obj.key === `addressTimeline`)[0]?.value
            const tempAR = M_StateData.filter(obj => obj.key === `autoRefresh`)[0]?.value
            const tempPage = M_StateData.filter(obj => obj.key === `page`)[0]?.value
            const tempHasMore = M_StateData.filter(obj => obj.key === `hasMore`)[0]?.value
            const tempSearchValue = M_StateData.filter(obj => obj.key === `searchValue`)[0]?.value
            if (tempOS || tempAT || tempAR || typeof tempPage === 'number' || typeof tempHasMore === 'boolean') {
                const os = tempOS || []
                const inferredPage = os.length > 0 ? Math.floor(os.length / pageSize) + 1 : 1
                const inferredHasMore = os.length > 0 ? (os.length % pageSize === 0) : true
                setOrderServices(os)
                setAddressTimeline(tempAT || [])
                setAutoRefresh(!!tempAR)
                setPage(typeof tempPage === 'number' ? tempPage : inferredPage)
                setHasMore(typeof tempHasMore === 'boolean' ? tempHasMore : inferredHasMore)
                setSearchValue(tempSearchValue || '')
                ctx.setIsLoad(false)
                return
            }
        }
        reloadOrderService()
    }

    const operationOrderService = useCallback((operation, lineOrder) => {
        const selectedLine = lineOrder || selectedOrderService

        if (operation !== 'create' && !selectedLine) {
            ctx.toastMessages('warning', t("SelectedLine"), '')
            return
        }

        const current = ctx.passValue instanceof Map ? ctx.passValue : new Map()
        const newMap = new Map(current)

        newMap.set('operation', operation)
        if (operation === 'create') {
            ctx.setPassValue(newMap)
            ctx2.newTab('createOrderService', t("CreateOrderService"))
            return
        }
        newMap.set('lines', selectedLine)
        ctx.setPassValue(newMap)

        if (operation === 'edit') {
            ctx2.newTab('createOrderService', t("EditOrderService"))
            return
        }
        if (operation === 'copy') {
            ctx2.newTab('createOrderService', t("Copy"))
            return
        }
        if (operation === 'view') {
            ctx2.newTab('viewOrderService', `${t("NumberOrder")} #${selectedLine.ServiceOrderId}`)
            return
        }
    }, [ctx, ctx2, selectedOrderService, t])

    const deleteOrderService = useCallback(() => {
        ctx.setIsLoad(true)
        const orderId = ctx.passValue.get('id').Id
        bufferServiceOrderService.delete(orderId).then((response) => {
            if (response.name === 'AxiosError') {
                ctx.toastMessages('error', response.response.data.Errors[0], '')
                ctx.setIsLoad(false)
                return
            }
            setDialogDelete(false)
            ctx.toastMessages('success', '', t("OrderServiceDelete"))
            ctx.setIsLoad(false)
            reloadOrderService()
        })
    }, [ctx, setDialogDelete])

    const fetchOrderServices = useCallback(async (reset = false, searchTerm = debouncedSearchValue) => {
        if ((!reset && (loading || isFetchingRef.current || !hasMore))) return

        const requestGeneration = reset
            ? ++requestGenerationRef.current
            : requestGenerationRef.current

        isFetchingRef.current = true
        setLoading(true)

        try {
            const targetPage = reset ? 1 : page
            const globalFilter = buildGlobalFilter(searchTerm)
            const query = `?page=${targetPage}&pagesize=${pageSize}${globalFilter ? `&filter=${encodeURIComponent(globalFilter)}` : ''}`
            const response = await bufferServiceOrderService.filter(query)

            if (requestGeneration !== requestGenerationRef.current) return

            if (response.name === 'AxiosError') {
                ctx.toastMessages('error', response.response?.data?.Errors?.[0] || '', '')
                return
            }

            const items = response.Items || []
            const totalPages = response.TotalPages

            setOrderServices(prev => (reset ? items : [...prev, ...items]))
            setPage(prev => (reset ? 2 : prev + 1))
            if (typeof totalPages === 'number') {
                setHasMore(targetPage < totalPages)
            } else {
                setHasMore(items.length === pageSize)
            }
        } finally {
            if (requestGeneration === requestGenerationRef.current) {
                setLoading(false)
                isFetchingRef.current = false
            }
        }
    }, [page, hasMore, loading, bufferServiceOrderService, ctx, debouncedSearchValue])

    const reloadOrderService = useCallback((searchTerm = debouncedSearchValue, showMessages = true) => {
        if (showMessages) {
            ctx.toastMessages("info", '', t("GetNewOrderServices"))
        }
        setHasMore(true)
        setPage(1)
        setOrderServices([])
        setSelectedOrderService(null)
        fetchOrderServices(true, searchTerm).finally(() => {
            ctx.setIsLoad(false)
            if (showMessages) {
                ctx.toastMessages('success', '', t("GetOrderServices"))
            }
        })
    }, [fetchOrderServices, ctx, t, debouncedSearchValue])

    useEffect(() => {
        reloadOrderServiceRef.current = reloadOrderService
    }, [reloadOrderService])

    useEffect(() => {
        const timeout = setTimeout(() => {
            setDebouncedSearchValue(searchValue.trim())
        }, 400)

        return () => clearTimeout(timeout)
    }, [searchValue])

    useEffect(() => {
        if (!searchInitializedRef.current) {
            searchInitializedRef.current = true
            return
        }

        reloadOrderServiceRef.current?.(debouncedSearchValue, false)
    }, [debouncedSearchValue])

    useEffect(() => {
        if (!sentinelRef.current) return
        const observer = new IntersectionObserver(
            entries => {
                if (entries[0]?.isIntersecting) {
                    fetchOrderServices()
                }
            },
            { root: null, rootMargin: '200px', threshold: 0 }
        )
        observer.observe(sentinelRef.current)
        return () => observer.disconnect()
    }, [fetchOrderServices])


    const changeAutoRefresh = () => {
        setAutoRefresh(prev => !prev)
    }

    const ListInformation = useMemo(() => [
        { Description: "DateHourServiceInformation", Icon: "calendar" },
        { Description: "CustomerInformation", Icon: "user" },
        { Description: "ServiceTypeInformation", Icon: "arrow-ramp-left" },
        { Description: "ContainerInformation", Icon: "box" },
        { Description: "EnrollmentNumberInformation", Icon: "square-0" },
        { Description: "WeightInformation", Icon: "weight" },
        { Description: "GeneratorInformation", Icon: "thunder" },
        { Description: "ADRInformation", Icon: "radioactive" },
        { Description: "BasculaInformation", Icon: "Bascula" },
        { Description: "PAAInformation", Icon: "animals" },
        { Description: "NotesInformation", Icon: "info" },
    ])

    const truncateText = (text, maxLength) => {
        if (text.length > maxLength) {
            return text.substring(0, maxLength) + '...'
        }
        return text
    }

    return (
        <div>
            <M_Header
                element={
                    <>
                        <div className='divHeaderListOrderService'>
                            <M_Button
                                tooltip={t("RefreshOrderServices")}
                                icon={<img src="/icons/svgsDaNet/refresh.svg"
                                    alt="OrderService"
                                    className='iconsMenuListOrderService'
                                />}
                                label={t("RefreshOrderServices")}
                                onClick={() => reloadOrderService()}
                            />
                            <M_Button
                                tooltip={t("AddOrderService")}
                                icon={<img src="/icons/svgsDaNet/add-circle.svg"
                                    style={{ filter: 'invert(42%) sepia(44%) saturate(4434%) hue-rotate(74deg) brightness(97%) contrast(101%)' }}
                                    alt="OrderService"
                                    className='iconsMenuListOrderService'
                                />}
                                label={t("AddOrderService")}
                                onClick={() => operationOrderService('create')}
                            />
                            <M_Button
                                tooltip={t("Copy")}
                                icon={<img src="/icons/svgsDaNet/copy.svg"
                                    alt="CopyOrderService"
                                    className='iconsMenuListOrderService'
                                />}
                                label={t("Copy")}
                                onClick={() => operationOrderService('copy')}
                            />
                            <M_Button
                                tooltip={t("InformationLabel")}
                                icon={<img src="/icons/svgsDaNet/help-circle.svg"
                                    style={{ filter: 'invert(87%) sepia(14%) saturate(7477%) hue-rotate(7deg) brightness(98%) contrast(102%)' }}
                                    alt="OrderService"
                                    className='iconsMenuListOrderService'
                                />}
                                label={t("InformationLabel")}
                                onClick={() => setDialogInformation(true)}
                            />
                            <div style={{ display: 'flex', alignItems: 'center', marginLeft: '15px' }}>
                                <MSwitchButton
                                    label={"Auto-Refresh"}
                                    labelColor={'var(--color-black'}
                                    onChange={changeAutoRefresh}
                                    checked={autoRefresh}
                                    fontSize={'13px'}
                                />
                            </div>
                        </div>
                        <div className='divGlobalFiltersOrderService'>
                            <div className='divInputSearchOrderServices'>
                                <M_InputTextField
                                    placeholder={t("Search")}
                                    height={'40px'}
                                    inputHeight={'20px'}
                                    iconStart={<img src="/icons/svgsDaNet/search.svg"
                                        style={{ filter: 'invert(16%) sepia(66%) saturate(5073%) hue-rotate(347deg) brightness(74%) contrast(116%)', width: '24px', marginRight: '10px' }}
                                        alt="OrderService"
                                    />}
                                    iconEnd={searchValue ? (
                                        <IconButton
                                            size="small"
                                            aria-label={t("Clear")}
                                            onClick={() => setSearchValue('')}
                                        >
                                            <Close fontSize="small" />
                                        </IconButton>
                                    ) : null}
                                    value={searchValue}
                                    onChange={(e) => setSearchValue(e.target.value)}
                                />
                            </div>
                        </div>
                    </>
                }
            />

            <div className='divGlobalListOrderServices'>
                {orderServices.map((value, index) => (
                    <div
                        key={index}
                        className={`divGlobalBlockOrdrService ${selectedOrderService?.Id === value.Id ? 'divGlobalBlockOrdrServiceSelected' : ''}`}
                        onClick={() => setSelectedOrderService(value)}
                    >
                        <div className='divInformationListOrderService'>
                            <div className='divLineInforamtionOrderService'>
                                <div style={{ backgroundColor: value.StateColor }} className='divStatusListOrderService'>
                                    {value.ServiceOrderState || "Inválido"}
                                </div>
                            </div>
                            <div className='divLineInforamtionOrderService'>
                                <img
                                    src="/icons/svgsDaNet/calendar.svg"
                                    style={{ filter: 'invert(8%) sepia(91%) saturate(5863%) hue-rotate(350deg) brightness(107%) contrast(112%)' }}
                                    className='iconsOrderServiceListInformation'
                                    alt="Calendar"
                                />
                                <b>  {new Date(value.TransportDate).toISOString().slice(0, 16).replace('T', ' ')}</b>
                            </div>
                            <div className='divLineInforamtionOrderService'>
                                <img
                                    src="/icons/svgsDaNet/user.svg"
                                    style={{ filter: 'invert(8%) sepia(91%) saturate(5863%) hue-rotate(350deg) brightness(107%) contrast(112%)' }}
                                    className='iconsOrderServiceListInformation'
                                    alt="User"
                                />
                                <b>{value.Customer}</b>
                            </div>
                            <div className='divLineInforamtionOrderService'>
                                <img
                                    src="/icons/svgsDaNet/arrow-ramp-left.svg"
                                    style={{ filter: 'invert(8%) sepia(91%) saturate(5863%) hue-rotate(350deg) brightness(107%) contrast(112%)' }}
                                    className='iconsOrderServiceListInformation'
                                    alt="ServiceType"
                                />
                                <b>{value.ServiceType}</b>
                            </div>

                            <div className='divLineInforamtionOrderService'>
                                {value.ServiceContainerResponse !== null ? (
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        <img
                                            src="/icons/svgsDaNet/box.svg"
                                            style={{ filter: 'invert(8%) sepia(91%) saturate(5863%) hue-rotate(350deg) brightness(107%) contrast(112%)' }}
                                            className='iconsOrderServiceListInformation'
                                            alt="Container"
                                        />
                                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                            <b>{value.ServiceContainerResponse.Capacity} {value.ServiceContainerResponse.TypeAbbreviation} </b>
                                            <img
                                                src="/icons/svgsDaNet/weight.svg"
                                                style={{ filter: 'invert(8%) sepia(91%) saturate(5863%) hue-rotate(350deg) brightness(107%) contrast(112%)', marginRight: '-2px', marginLeft: '10px' }}
                                                className='iconsOrderServiceListInformation'
                                                alt="Weight"
                                            />
                                            <b style={{ marginRight: '5px', marginLeft: '5px' }}>{value.ServiceContainerResponse.NetWeight}Kg</b>
                                        </div>
                                    </div>
                                ) : value.ROROInformation ? (
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        <img
                                            src="/icons/svgsDaNet/square-0.svg"
                                            style={{ filter: 'invert(8%) sepia(91%) saturate(5863%) hue-rotate(350deg) brightness(107%) contrast(112%)' }}
                                            className='iconsOrderServiceListInformation'
                                            alt="plateNumber"
                                        />
                                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                            <b>{value.ROROInformation.EnrollmentNumber}</b>
                                            <img
                                                src="/icons/svgsDaNet/weight.svg"
                                                style={{ filter: 'invert(8%) sepia(91%) saturate(5863%) hue-rotate(350deg) brightness(107%) contrast(112%)', marginRight: '-2px', marginLeft: '10px' }}
                                                className='iconsOrderServiceListInformation'
                                                alt="Weight"
                                            />
                                            <b style={{ marginRight: '5px', marginLeft: '5px' }}>{value.ROROInformation.LoadWeight}Kg</b>
                                        </div>
                                    </div>
                                ) : (
                                    <div>Nenhuma informação disponível</div>
                                )}

                            </div>
                            {value.TransportResourceResponse.Generator === true ?
                                <div className='divLineInforamtionOrderService'>
                                    <img
                                        src="/icons/svgsDaNet/thunder.svg"
                                        style={{ filter: 'invert(8%) sepia(91%) saturate(5863%) hue-rotate(350deg) brightness(107%) contrast(112%)' }}
                                        className='iconsOrderServiceListInformation'
                                        alt="Generator"
                                    />
                                    <b>{t("WithGenerator")}</b>
                                </div>
                                : null
                            }

                            {value.TransportResourceResponse.ADR === true ?
                                <div className='divLineInforamtionOrderService'>
                                    <img
                                        src="/icons/svgsDaNet/radioactive.svg"
                                        style={{ filter: 'invert(8%) sepia(91%) saturate(5863%) hue-rotate(350deg) brightness(107%) contrast(112%)' }}
                                        className='iconsOrderServiceListInformation'
                                        alt="ADR"
                                    />
                                    <b>{t("WithADR")}</b>
                                </div>
                                : null
                            }

                            {value.TransportResourceResponse.Bascula === true ?
                                <div className='divLineInforamtionOrderService'>
                                    <img
                                        src="/icons/svgsDaNet/Bascula.svg"
                                        style={{ filter: 'invert(8%) sepia(91%) saturate(5863%) hue-rotate(350deg) brightness(107%) contrast(112%)' }}
                                        className='iconsOrderServiceListInformation'
                                        alt="Bascula"
                                    />
                                    <b>{t("WithBascula")} </b>
                                </div>
                                : null
                            }

                            {value.TransportResourceResponse.PAA === true ?
                                <div className='divLineInforamtionOrderService'>
                                    <img
                                        src="/icons/svgsDaNet/animals.svg"
                                        style={{ filter: 'invert(8%) sepia(91%) saturate(5863%) hue-rotate(350deg) brightness(107%) contrast(112%)' }}
                                        className='iconsOrderServiceListInformation'
                                        alt="Animals"
                                    />
                                    <b>{t("WithPAA")} </b>
                                </div>
                                : null
                            }

                            {value.Reload === true ?
                                <div className='divLineInforamtionOrderService'>
                                    <img
                                        src="/icons/svgsDaNet/reloadOR.svg"
                                        style={{ filter: 'invert(8%) sepia(91%) saturate(5863%) hue-rotate(350deg) brightness(107%) contrast(112%)' }}
                                        className='iconsOrderServiceListInformation'
                                        alt="Reload"
                                    />
                                    <b>Com Reload </b>
                                </div>
                                : null
                            }

                            {value.NotesInternal !== null ?
                                <div className='divLineInforamtionOrderService'>
                                    <img
                                        src="/icons/svgsDaNet/info.svg"
                                        style={{ filter: 'invert(8%) sepia(91%) saturate(5863%) hue-rotate(350deg) brightness(107%) contrast(112%)' }}
                                        className='iconsOrderServiceListInformation'
                                        alt="Info"
                                    />
                                    <b>{value.NotesInternal}</b>
                                </div>
                                : null
                            }

                        </div>
                        <div className='divTimelineListOrderService'>
                            <MTimeLine
                                valueTimeline={value.Address.slice().sort((a, b) => a.Sequence - b.Sequence) // Cria uma cópia e ordena
                                    .map(value => ({
                                        marker: value.AddressType === "Pickup"
                                            ? <img src="/icons/svgsDaNet/arrowTop.svg" alt="arrowTop" />
                                            : value.AddressType === "Charge/Discharge"
                                                ? <img src="/icons/svgsDaNet/forkTimeline.svg" alt="forkTimeline" />
                                                : value.AddressType === "Delivery"
                                                    ? <img src="/icons/svgsDaNet/arrowDown.svg" alt="arrowDown" />
                                                    : <img src="/icons/svgsDaNet/pin.svg" alt="defaultArrow" />,
                                        // falta adicionar a variavel com o nome AddressZipPlace no header da timeline (pendente BE)
                                        header: truncateText(value.AddressName, 20), //nome da morada
                                        subHeader: (
                                            <>
                                                {value.AddressPlace} <br />
                                                {value?.Distance != null ? value.Distance.toFixed(2) : "0"}Km - {value.Time != null ? (value.Time < 60 ? `${value.Time} Min` : `${(value.Time / 60).toFixed(2)} H`) : "0 Min"}
                                            </>
                                        ), // zona da morada
                                        toolTipLabel: value.AddressName
                                    }))
                                }
                                timeLineRowPosition={'bottom'}
                                subHeaderSize={'12px'}
                            />

                        </div>
                        <div className='divIconsListOrderService'>
                            <Tooltip title={t("Edit")} placement='left' >
                                <img
                                    src="/icons/svgsDaNet/edit-2.svg"
                                    className='iconsListOrderService'
                                    alt="Edit"
                                    onClick={(event) => {
                                        event.stopPropagation()
                                        operationOrderService('edit', value)
                                    }}
                                />
                            </Tooltip>
                            <Tooltip title={t("Copy")} placement='left' >
                                <img
                                    src="/icons/svgsDaNet/copy.svg"
                                    className='iconsListOrderService'
                                    alt="Copy"
                                    onClick={(event) => {
                                        event.stopPropagation()
                                        operationOrderService('copy', value)
                                    }}
                                />
                            </Tooltip>
                            <Tooltip title={t("Delete")} placement='left' >
                                <img
                                    src="/icons/svgsDaNet/Trash-2.svg"
                                    style={{ filter: 'invert(8%) sepia(91%) saturate(5863%) hue-rotate(350deg) brightness(107%) contrast(112%)' }}
                                    className='iconsListOrderService'
                                    alt="Delete"
                                    onClick={(event) => {
                                        event.stopPropagation()
                                        setDialogDelete(true)
                                        ctx.passValue.set('id', value)
                                    }}
                                />
                            </Tooltip>
                            <Tooltip title={t("view")} placement='left' >
                                <img src="/icons/svgsDaNet/eye.svg"
                                    style={{ filter: 'invert(36%) sepia(58%) saturate(4292%) hue-rotate(197deg) brightness(101%) contrast(108%)' }}
                                    className='iconsListOrderService'
                                    alt="View"
                                    onClick={(event) => {
                                        event.stopPropagation()
                                        operationOrderService('view', value)
                                    }}
                                />
                            </Tooltip>
                        </div>
                    </div>
                ))}
                <div ref={sentinelRef} style={{ height: 1 }} />
                {loading && <div style={{ textAlign: "center", padding: "10px" }}>{t("Loading")}...</div>}
                {!hasMore && <div style={{ textAlign: "center", padding: "10px" }}>{t("NoMoreResults")}</div>}
            </div>

            {/* MessageBoard de eliminar Ordem de Serviço */}
            <M_MessageBoard
                open={dialogDelete}
                onClose={() => setDialogDelete(false)}
                title={t("DeleteOrderServiceNumber")}
                warning
                body={
                    <div>
                        {t("TitleConfirmDelete")}?
                        <br /><br />
                        {t("BodyConfirmDelete")}!
                    </div>
                }
                footer={
                    <div className='divAlignFooterCircle'>
                        <M_Button backgroundColor="var(--color-red-dark)" labelColor={"var(--color-white)"} label={t("ButtonCancel")} hoverEffect onClick={() => { setDialogDelete(false) }} />
                        <M_Button backgroundColor="var(--color-green-dark)" labelColor={"var(--color-white)"} label={t("ButtonConfirm")} hoverEffect onClick={() => deleteOrderService()} />
                    </div>
                }
            />

            {/* MessageBoard de Informação da Ordem de Serviço */}
            <M_MessageBoard
                open={dialogInformation}
                onClose={() => setDialogInformation(false)}
                title={
                    <div className='divTitleMessageBoardInformation'>
                        <img src="/icons/svgsDaNet/help-circle.svg"
                            style={{ filter: 'invert(87%) sepia(14%) saturate(7477%) hue-rotate(7deg) brightness(98%) contrast(102%)', width: '35px', marginRight: '10px' }}
                            alt="OrderService"
                        />
                        {t("InformationLabel")}
                    </div>
                }
                info
                body={
                    <div className='divDisplayInformationOrderService'>
                        {ListInformation.map((value, key) => (

                            <div key={key} className='divAlignContainerInformationOrderService'>
                                <img
                                    src={`/icons/svgsDaNet/${value.Icon}.svg`}
                                    style={{ filter: 'invert(8%) sepia(91%) saturate(5863%) hue-rotate(350deg) brightness(107%) contrast(112%)', width: '27px' }}
                                    alt={value.Icon}
                                />

                                <b>{t(value.Description)}</b>
                            </div>
                        ))}
                    </div>
                }
                footer={
                    <div className='divAlignFooterCircle'>
                        <M_Button backgroundColor="var(--color-red-dark)" labelColor={"var(--color-white)"} label={t("Close")} hoverEffect onClick={() => { setDialogInformation(false) }} />
                    </div>
                }
            />
        </div>
    )
}
